import { Target, TrendingUp, AlertTriangle, ChevronRight, Crosshair } from "lucide-react";
import { P, SectionTitle } from "./report-utils";

export function CoachingTab({ archetype, risks, consistency, aps }) {
    return (
        <div data-testid="section-coaching">
            <SectionTitle icon={Crosshair} title="What This Means for Coaching" subtitle="Decision guidance for the coaching engagement" id="coaching" />

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="rounded-xl border border-blue-100 bg-white p-6 shadow-sm">
                    <div className="flex items-center gap-2.5 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Target className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">Coaching Approach</p>
                    </div>
                    <p className="text-sm font-semibold text-slate-800 mb-2">{archetype?.coaching_style}</p>
                    <p className="text-sm text-slate-500 leading-relaxed">{archetype?.practical_approach}</p>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-white p-6 shadow-sm">
                    <div className="flex items-center gap-2.5 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                            <TrendingUp className="h-4 w-4 text-emerald-600" />
                        </div>
                        <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Key Growth Levers</p>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">{archetype?.growth_triggers}</p>
                    <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600">
                        <ChevronRight className="h-3 w-3" />
                        <span>Focus here for fastest ROI</span>
                    </div>
                </div>
            </div>

            {risks.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-6 mb-6 shadow-sm">
                    <div className="flex items-center gap-2.5 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                        </div>
                        <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Risks to Watch</p>
                    </div>
                    <div className="space-y-3">
                        {risks.map((r, i) => (
                            <div key={r.name} className="flex items-start gap-3">
                                <div className="w-5 h-5 rounded-full bg-amber-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-[0.6rem] font-bold text-amber-700">{i + 1}</span>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-amber-900">{r.name}</p>
                                    <p className="text-xs text-amber-700/70 leading-relaxed mt-0.5">{r.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                        <Crosshair className="h-4 w-4 text-violet-600" />
                    </div>
                    <p className="text-xs font-bold text-violet-700 uppercase tracking-wider">Suggested Next Actions</p>
                </div>
                <div className="space-y-3">
                    {archetype?.coaching_strategy && (
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 mt-2" />
                            <p className="text-sm text-slate-600 leading-relaxed">{archetype.coaching_strategy}</p>
                        </div>
                    )}
                    {archetype?.predicted_roi && (
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 mt-2" />
                            <p className="text-sm text-slate-600 leading-relaxed"><span className="font-semibold text-slate-800">Expected ROI:</span> {archetype.predicted_roi}</p>
                        </div>
                    )}
                    {consistency?.interpretation && (
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 mt-2" />
                            <p className="text-sm text-slate-600 leading-relaxed"><span className="font-semibold text-slate-800">Consistency Note:</span> {consistency.interpretation}</p>
                        </div>
                    )}
                    {aps?.description && (
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 mt-2" />
                            <p className="text-sm text-slate-600 leading-relaxed"><span className="font-semibold text-slate-800">Action Readiness:</span> {aps.description}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
